import { getStorageItem } from '../storage';
import { getAxiosInstance, getToken } from './api';
import { AppInsightsTypes } from './AppInsightsTypes';
import { addEnvQueryParam, getErrorResponse } from './helpers';
import { PaginatedResponse } from './PaginatedReponse';
import { HttpStatusCode } from './thunkActionTypes';
import { getClientTelemetryHeaders } from '../telemetry';
import axios, { CancelToken, Method } from 'axios';

export type AuthenticatedJsonRequestOptions<TBody = unknown> = {
  method: Method;
  url: string;
  data?: TBody;
  cancelToken?: CancelToken;
  clientAction?: string;
  sensitive?: boolean;
  maxResponseBytes?: number;
};

/**
 * Shared authenticated request primitive for API adapters that need request
 * cancellation or methods beyond the legacy convenience wrappers.
 */
export const axiosAuthenticatedJsonRequest = async <TResponse, TBody = unknown>(
  options: AuthenticatedJsonRequestOptions<TBody>
): Promise<TResponse> => {
  const requestUrl = addEnvQueryParam(options.url);
  const headers = await getRequestHeaders(undefined, options.clientAction, {
    method: options.method,
    url: requestUrl,
  });

  if (options.data !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.sensitive && options.method === 'get') headers['Cache-Control'] = 'no-store';

  try {
    const axiosInstance = getAxiosInstance();
    if (!axiosInstance) {
      throw new Error('Axios instance not initialized');
    }

    const response = await axiosInstance({
      method: options.method,
      url: requestUrl,
      data: options.data,
      headers,
      cancelToken: options.cancelToken,
      ...(options.sensitive ? { carecircleSensitive: true, timeout: 30000 } : {}),
      ...(options.maxResponseBytes ? { maxContentLength: options.maxResponseBytes } : {}),
    });

    return response.data;
  } catch (error) {
    if (options.sensitive) throw new Error('survey_recovery_unavailable');
    if (axios.isCancel(error)) {
      throw error;
    }
    logApiCallError(error, options.url);
    throw getErrorResponse(error);
  }
};

const getRequestHeaders = async (
  doNotRenewToken?: boolean,
  clientAction?: string,
  requestInfo?: { method?: string; url?: string }
) => {
  const authId = await getToken(doNotRenewToken);
  const storedSessionId = await getStorageItem(AppInsightsTypes.SESSION_ID);
  const userIdResult = await getStorageItem(AppInsightsTypes.USER_ID);
  const clientTelemetryHeaders = await getClientTelemetryHeaders({
    clientAction,
    method: requestInfo?.method,
    url: requestInfo?.url,
  });

  const clientSessionId =
    clientTelemetryHeaders['x-client-session-id'] ?? storedSessionId;

  return {
    Authorization: 'Bearer ' + authId,
    Accept: 'application/json',
    'x-carecircle-sessionid': clientSessionId,
    'x-carecircle-userid': userIdResult,
    ...clientTelemetryHeaders,
  };
};

/**
 * AXIOS Get request
 * - url: the relative endpoint url e.g category/getCategories
 */
export const axiosGetRequest = async <T>(
  url: string,
  doNotRenewToken?: boolean,
  clientAction?: string
): Promise<T> => {
  const requestUrl = addEnvQueryParam(url);
  const headers = await getRequestHeaders(doNotRenewToken, clientAction, {
    method: 'get',
    url: requestUrl,
  });

  try {
    const axiosInstance = getAxiosInstance();
    if (!axiosInstance) {
      throw new Error('Axios instance not initialized');
    }

    const response = await axiosInstance({
      method: 'get',
      url: requestUrl,
      headers,
    });

    return response.data;
  } catch (error) {
    logApiCallError(error, url);
    throw getErrorResponse(error);
  }
};
/**
 * AXIOS Get request
 * - url: the relative endpoint url e.g category/getCategories
 * - x-ms-continuation: Pagination parameter which goes in header. If it is present in response, the request has more records.
 */
export const axiosGetPaginatedRequest = async <T>(
  url: string,
  xMsContinuationToken?: string,
  doNotRenewToken?: boolean,
  clientAction?: string
): Promise<PaginatedResponse<T>> => {
  const requestUrl = addEnvQueryParam(url);
  const headers = await getRequestHeaders(doNotRenewToken, clientAction, {
    method: 'get',
    url: requestUrl,
  });

  if (xMsContinuationToken) headers['x-ms-continuation'] = xMsContinuationToken;

  try {
    const axiosInstance = getAxiosInstance();
    if (!axiosInstance) {
      throw new Error('Axios instance not initialized');
    }

    const response = await axiosInstance({
      method: 'get',
      url: requestUrl,
      headers: headers,
    });

    const paginatedResponse: PaginatedResponse<T> = {
      data: response.data,
      xMsContinuationToken: response.headers['x-ms-continuation'],
    };
    return paginatedResponse;
  } catch (error) {
    logApiCallError(error, url);
    throw getErrorResponse(error);
  }
};

/**
 * AXIOS paginated Post request
 * - url: the relative endpoint url e.g category/getCategories
 * - x-ms-continuation: Pagination parameter which goes in header. If it is present in response, the request has more records.
 */
export const axiosPostPaginatedRequest = async <T1, T2>(
  url: string,
  data?: T1,
  xMsContinuationToken?: string,
  clientAction?: string,
  additionalHeaders?: Record<string, string>,
  sensitive = false
): Promise<PaginatedResponse<T2>> => {
  const requestUrl = addEnvQueryParam(url);
  const headers = await getRequestHeaders(undefined, clientAction, {
    method: 'post',
    url: requestUrl,
  });

  if (xMsContinuationToken) headers['x-ms-continuation'] = xMsContinuationToken;
  if (additionalHeaders) Object.assign(headers, additionalHeaders);

  try {
    const axiosInstance = getAxiosInstance();
    if (!axiosInstance) {
      throw new Error('Axios instance not initialized');
    }

    const response = await axiosInstance({
      method: 'post',
      url: requestUrl,
      data,
      headers,
      ...(sensitive ? { carecircleSensitive: true } : {}),
    });

    const paginatedResponse: PaginatedResponse<T2> = {
      data: response.status === HttpStatusCode.no_content ? [] : response.data,
      xMsContinuationToken: response.headers['x-ms-continuation'],
      statusCode: response.status,
    };
    return paginatedResponse;
  } catch (error) {
    if (sensitive) throw new Error('survey_outcome_unknown');
    logApiCallError(error, url);
    throw getErrorResponse(error);
  }
};

/**
 * AXIOS POST request
 * - url: the relative endpoint url e.g category/getCategories
 * - data: the body object
 */
export const axiosPostRequest = async <T1, T2>(
  url: string,
  data?: T1,
  responseType?: any,
  clientAction?: string
): Promise<T2> => {
  const requestUrl = addEnvQueryParam(url);
  const headers = await getRequestHeaders(undefined, clientAction, {
    method: 'post',
    url: requestUrl,
  });

  return new Promise((resolve, reject) => {
    const axiosInstance = getAxiosInstance();

    axiosInstance &&
      axiosInstance({
        method: 'post',
        url: requestUrl,
        data,
        responseType,
        headers,
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(getErrorResponse(error));
          logApiCallError(error, url);
        });
  });
};

export const axiosMultipartPostRequest = async <T1, T2>(
  url: string,
  data?: T1,
  clientAction?: string,
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
): Promise<T2> => {
  const requestUrl = addEnvQueryParam(url);
  const headers = await getRequestHeaders(undefined, clientAction, {
    method: 'post',
    url: requestUrl,
  });
  headers['Content-Type'] = 'multipart/form-data';

  return new Promise((resolve, reject) => {
    const axiosInstance = getAxiosInstance();
    axiosInstance &&
      axiosInstance({
        method: 'post',
        url: requestUrl,
        data,
        headers,
        onUploadProgress,
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(getErrorResponse(error));
          logApiCallError(error, url);
        });
  });
};

/**
 * AXIOS DELETE request
 * - url: the relative endpoint url e.g category/getCategories
 * - data: the body object, may be optional
 */
export const axiosDeleteRequest = async <T1, T2>(
  url: string,
  data?: T1,
  clientAction?: string
): Promise<T2> => {
  const requestUrl = addEnvQueryParam(url);
  const headers = await getRequestHeaders(undefined, clientAction, {
    method: 'delete',
    url: requestUrl,
  });
  headers['Content-Type'] = 'application/json';

  return new Promise((resolve, reject) => {
    const axiosInstance = getAxiosInstance();
    axiosInstance &&
      axiosInstance({
        method: 'delete',
        url: requestUrl,
        data,
        headers,
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(getErrorResponse(error));
          logApiCallError(error, url);
        });
  });
};

/**
 * AXIOS PUT request
 * - url: the relative endpoint url e.g category/getCategories
 * - data: the body object, may be optional
 */
export const axiosPutRequest = async <T1, T2>(
  url: string,
  data?: T1,
  clientAction?: string
): Promise<T2> => {
  const requestUrl = addEnvQueryParam(url);
  const headers = await getRequestHeaders(undefined, clientAction, {
    method: 'put',
    url: requestUrl,
  });
  headers['Content-Type'] = 'application/json';

  return new Promise((resolve, reject) => {
    const axiosInstance = getAxiosInstance();
    axiosInstance &&
      axiosInstance({
        method: 'put',
        url: requestUrl,
        data,
        headers,
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(getErrorResponse(error));
          logApiCallError(error, url);
        });
  });
};

/**
 * AXIOS PATCH request
 * - url: the relative endpoint url e.g category/getCategories
 * - data: the body object, may be optional
 */
export const axiosPatchRequest = async <T1, T2>(
  url: string,
  data?: T1,
  responseType?: any,
  clientAction?: string
): Promise<T2> => {
  const requestUrl = addEnvQueryParam(url);
  const headers = await getRequestHeaders(undefined, clientAction, {
    method: 'patch',
    url: requestUrl,
  });
  headers['Content-Type'] = 'application/json';

  return new Promise((resolve, reject) => {
    const axiosInstance = getAxiosInstance();
    axiosInstance &&
      axiosInstance({
        method: 'patch',
        url: requestUrl,
        data,
        responseType,
        headers,
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          console.error('[axiosPatchRequest] error: ', error);
          reject(getErrorResponse(error));
          logApiCallError(error, url);
        });
  });
};

const logApiCallError = (error: any, url: string) => {
  // getAppInsightsInstance().trackException({
  //   exception: error,
  //   properties: { url: url },
  // });
  try {
    const status = error?.response?.status;
    const statusText = error?.response?.statusText;
    const method = error?.config?.method;
    const fullUrl = error?.config?.baseURL
      ? `${error.config.baseURL}${error.config.url ?? ''}`
      : error?.config?.url;
    const data = error?.response?.data;

    const dataPreview =
      typeof data === 'string'
        ? data.slice(0, 500)
        : data
        ? JSON.stringify(data).slice(0, 500)
        : undefined;

    console.error('[api] request failed', {
      method,
      url,
      fullUrl,
      status,
      statusText,
      data: dataPreview,
    });
  } catch {
    // Ignore logging failures
  }
};
